/**
 * What the payment modal waits for once the provider has the payment, and what it may say when the
 * wait ends — the one decision behind `modals/TopUpModal.vue`'s last steps, pure so a spec pins it.
 *
 * **The provider's answer in the browser says what it collected, never what the invoicing service
 * recorded.** A card that succeeded is credited when the top-up reads `completed`; a payment the
 * provider answered `processing` is a direct debit on its way — or, briefly, a card — and the
 * top-up says which once ledger's event lands: `completed` for the card, `payment_processing_since`
 * for the debit. So the modal re-reads the top-up for a bounded while and draws only what the row
 * says; past the deadline it says what is true of every case, that the payment is being confirmed.
 */

import { isCollecting } from './status'

/**
 * How a wait ended:
 * - `paid` — the top-up reads `completed`, and its credits are in the wallet;
 * - `declined` — a failure was recorded after the wait began: the payment just confirmed was
 *   refused, and the top-up is payable again;
 * - `collecting` — ledger says a debit of it is being collected (`payment_processing_since`), so
 *   the credits land when the bank answers, in days;
 * - `processing` — none of these yet: the payment is being confirmed, and the top-up is in the
 *   history.
 */
export type WaitOutcome = 'paid' | 'declined' | 'collecting' | 'processing'

/** What a re-read of the top-up has to answer for the wait to decide. */
export interface WaitedPurchase {
  status: string
  payment_processing_since?: string | null
  payment_failed_at?: string | null
}

/**
 * Whether the top-up records a failure later than `failedBefore` — its `payment_failed_at` as read
 * before the payer confirmed — which is the refusal of the payment being waited for. Both instants
 * are the invoicing service's, so no clock of this browser is compared. `undefined` watches for no
 * refusal at all; `null` is a top-up that had failed never before.
 */
export function refusedSince(purchase: WaitedPurchase, failedBefore: string | null | undefined): boolean {
  if (failedBefore === undefined || purchase.status !== 'open' || !purchase.payment_failed_at) return false
  return failedBefore === null || Date.parse(purchase.payment_failed_at) > Date.parse(failedBefore)
}

/**
 * What one read of the top-up settles, or `null` while it settles nothing.
 *
 * `acceptCollecting` is whether a debit in flight ends the wait: after the provider's `processing`
 * it does, since that is the answer the wait is for; after a `succeeded` only `completed` does.
 * `failedBefore` is [`refusedSince`]'s: a refusal recorded since the wait began settles it as
 * `declined`, before a stamp it may sit beside.
 */
export function settledBy(
  purchase: WaitedPurchase,
  acceptCollecting: boolean,
  failedBefore?: string | null,
): Exclude<WaitOutcome, 'processing'> | null {
  if (purchase.status === 'completed') return 'paid'
  if (refusedSince(purchase, failedBefore)) return 'declined'
  if (acceptCollecting && isCollecting(purchase)) return 'collecting'
  return null
}

/** The first pause between two reads, in milliseconds. */
export const FIRST_DELAY_MS = 1_000
/** The longest pause between two reads, in milliseconds. */
export const LONGEST_DELAY_MS = 8_000

export interface WaitOptions {
  /** Whether a debit in flight ends the wait (see [`settledBy`]). */
  acceptCollecting: boolean
  /** The top-up's `payment_failed_at` before the payer confirmed (see [`refusedSince`]). */
  failedBefore?: string | null
  /** How long the reads go on before the wait ends on `processing`, in milliseconds. */
  timeoutMs: number
  sleep: (ms: number) => Promise<void>
  now?: () => number
  /** Whether nobody is waiting any more — the modal closed. The wait then stops reading. */
  stopped?: () => boolean
}

/** How a wait ended, and the top-up as the last read answered it. */
export interface WaitResult<P> {
  /** `null` when the wait was stopped before anything settled. */
  outcome: WaitOutcome | null
  purchase: P | null
}

/**
 * Re-read the top-up until a read settles it, on a backoff from [`FIRST_DELAY_MS`] growing by half
 * up to [`LONGEST_DELAY_MS`], for at most `timeoutMs`. A read that fails is thrown to the caller:
 * the modal shows the server's words rather than guessing.
 */
export async function waitForOutcome<P extends WaitedPurchase>(
  read: () => Promise<P>,
  options: WaitOptions,
): Promise<WaitResult<P>> {
  const now = options.now ?? Date.now
  const stopped = options.stopped ?? (() => false)
  const deadline = now() + options.timeoutMs
  let delay = FIRST_DELAY_MS
  let last: P | null = null

  while (!stopped()) {
    last = await read()
    const settled = settledBy(last, options.acceptCollecting, options.failedBefore)
    if (settled) return { outcome: settled, purchase: last }
    if (now() + delay > deadline) return { outcome: 'processing', purchase: last }
    await options.sleep(delay)
    delay = Math.min(Math.round(delay * 1.5), LONGEST_DELAY_MS)
  }
  return { outcome: null, purchase: last }
}
